import { useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import api from '../lib/api-client';
import { logger } from '../utils/logger';
import { toast } from 'sonner';

const EXPO_TOKEN_STORAGE_KEY = 'pending_expo_push_token';
const EXPO_PLATFORM_STORAGE_KEY = 'pending_expo_platform';
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 2000; // 2 seconds

export function ExpoTokenHandler() {
  const { user } = useAuth();
  const hasRegisteredToken = useRef(false);
  const retryCount = useRef(0);
  const retryTimer = useRef<NodeJS.Timeout | null>(null);

  // Function to register token with backend
  const registerTokenWithBackend = async (token: string, platform: string, attempt = 1) => {
    logger.info('🔍 [ExpoTokenHandler] registerTokenWithBackend called', {
      hasToken: !!token,
      platform,
      attempt,
      userId: user?._id,
      userLoggedIn: !!user
    });

    if (!token) {
      logger.error('❌ [ExpoTokenHandler] No token provided to registerTokenWithBackend');
      return;
    }

    if (!user) {
      logger.info('📦 [ExpoTokenHandler] User not logged in, storing token for later');
      logger.info(`   Token: ${token.substring(0, 30)}...`);
      logger.info(`   Platform: ${platform}`);
      localStorage.setItem(EXPO_TOKEN_STORAGE_KEY, token);
      localStorage.setItem(EXPO_PLATFORM_STORAGE_KEY, platform);
      toast.info('Push token stored. Will register after login.');
      return;
    }

    try {
      logger.info(`📤 [ExpoTokenHandler] Making API call to register token (Attempt ${attempt}/${MAX_RETRY_ATTEMPTS})`);
      logger.info(`   URL: ${api.defaults.baseURL}/expo/update-push-token`);
      logger.info(`   Token: ${token.substring(0, 30)}...`);
      logger.info(`   Platform: ${platform}`);
      logger.info(`   User: ${user.firstName} ${user.lastName} (${user._id})`);
      logger.info(`   Device: ${navigator.userAgent.substring(0, 50)}...`);
      
      const response = await api.post('/expo/update-push-token', {
        expoPushToken: token,
        platform,
        device: navigator.userAgent
      });
      
      logger.info('✅ [ExpoTokenHandler] Token registered successfully!', response.data);
      logger.info(`   Token count: ${response.data.tokenCount || 'unknown'}`);
      hasRegisteredToken.current = true;
      retryCount.current = 0;
      
      // Clear stored token after successful registration
      localStorage.removeItem(EXPO_TOKEN_STORAGE_KEY);
      localStorage.removeItem(EXPO_PLATFORM_STORAGE_KEY);
    } catch (error: any) {
      logger.error(`❌ [ExpoTokenHandler] Failed to update Expo push token (Attempt ${attempt}/${MAX_RETRY_ATTEMPTS}):`, error);
      logger.error(`   Error message: ${error.message}`);
      logger.error(`   Status: ${error.response?.status}`);
      logger.error(`   Response: ${JSON.stringify(error.response?.data)}`);
      
      // Retry if we haven't exceeded max attempts
      if (attempt < MAX_RETRY_ATTEMPTS) {
        logger.info(`🔄 [ExpoTokenHandler] Retrying token registration in ${RETRY_DELAY / 1000}s...`);
        retryTimer.current = setTimeout(() => {
          registerTokenWithBackend(token, platform, attempt + 1);
        }, RETRY_DELAY);
      } else {
        logger.error('❌ [ExpoTokenHandler] Max retry attempts reached. Token will be stored for next login.');
        // Store for next time
        localStorage.setItem(EXPO_TOKEN_STORAGE_KEY, token);
        localStorage.setItem(EXPO_PLATFORM_STORAGE_KEY, platform);
      }
    }
  };

  // Effect to handle token received from native app
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleExpoPushToken = async (event: CustomEvent) => {
      const { token, platform } = event.detail;
      
      logger.info('📱 [ExpoTokenHandler] Expo Push Token Received', { 
        token: token?.substring(0, 20) + '...', 
        platform, 
        isLoggedIn: !!user,
        userId: user?._id 
      });
      
      if (!token) {
        logger.warn('⚠️  [ExpoTokenHandler] No Expo push token received');
        return;
      }

      await registerTokenWithBackend(token, platform);
    };

    window.addEventListener('expoPushTokenReceived', handleExpoPushToken as EventListener);

    if (window.isNativeApp) {
      logger.info('📱 [ExpoTokenHandler] Native app detected, requesting push token...');
      window.requestPushToken?.();
    } else {
      logger.info('🌐 [ExpoTokenHandler] Web app detected (not requesting Expo token)');
    }

    return () => {
      window.removeEventListener('expoPushTokenReceived', handleExpoPushToken as EventListener);
    };
  }, [user]);

  // Effect to register stored token when user logs in
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // If user just logged in and we haven't registered the token yet
    if (user && !hasRegisteredToken.current) {
      const storedToken = localStorage.getItem(EXPO_TOKEN_STORAGE_KEY);
      const storedPlatform = localStorage.getItem(EXPO_PLATFORM_STORAGE_KEY);
      
      if (storedToken && storedPlatform) {
        logger.info('🔄 [ExpoTokenHandler] User logged in, registering stored Expo token...');
        registerTokenWithBackend(storedToken, storedPlatform);
      } else if (window.isNativeApp && window.expoPushToken) {
        // Token might be in window object but not localStorage
        logger.info('🔄 [ExpoTokenHandler] User logged in, registering token from window object...');
        registerTokenWithBackend(window.expoPushToken, window.devicePlatform || 'unknown');
      } else if (window.isNativeApp) {
        // Request token again in case we missed it
        logger.info('🔄 [ExpoTokenHandler] User logged in, requesting push token again...');
        window.requestPushToken?.();
      }
    }
    
    // Reset flag when user logs out
    if (!user) {
      hasRegisteredToken.current = false;
    }
  }, [user]);

  return null;
}

declare global {
  interface Window {
    isNativeApp?: boolean;
    requestPushToken?: () => void;
    expoPushToken?: string;
    devicePlatform?: string;
  }
}